import { Injectable } from '@angular/core';
import { HttpClient, HttpContext, HttpErrorResponse } from '@angular/common/http';
import { Router } from '@angular/router';
import { BehaviorSubject, Subject, firstValueFrom } from 'rxjs';
import { environment } from 'src/environments/environment';
import { LocalStorageService } from 'src/app/common/service/storage.service';
import { UserDomainType } from 'src/app/common/enum/user-domain';
import { DepartmentId } from 'src/app/common/enum/department';
import { byPassErrorMessage } from '../core-interceptor/core-interceptor.service';
import { AUTH_ROUTES } from './auth-session.constants';

/** Result of a sign-in or session check. */
export interface AuthResult {
  ok: boolean;
  /** Message from the API when ok = false (e.g. "User not linked with Cereus App"). */
  message?: string;
  user?: any;
  /** SQL users with MFA on: the password was accepted and a PIN was e-mailed. */
  mfaRequired?: boolean;
  /** Masked e-mail the PIN was sent to. */
  mfaEmail?: string;
}

/**
 * Cookie-based session for both sign-in methods:
 *  - SQL:  POST {apiUrl}/auth/login with user name + password.
 *  - SSO:  full-page redirect to {apiUrl}/auth/sso/login; the API signs in with
 *          Entra ID (OpenID Connect, code + PKCE) and redirects back to the portal.
 * Either way the API sets an HttpOnly session cookie. The browser never holds
 * an access token, and every state-changing call carries the CSRF header
 * (see CredentialsInterceptor).
 *
 * The user profile and permissions are still cached in local storage
 * ('cereus-token' / 'cereus-cache') because the rest of the portal reads them
 * from there. They are display data only; the cookie is what authorizes calls.
 */
@Injectable({ providedIn: 'root' })
export class AuthSessionService {
  private readonly authUrl = `${environment.apiUrl}`;
  private readonly quiet = () => new HttpContext().set(byPassErrorMessage, true);

  /** Entra object id of the signed-in user (SSO users only), used by SignalR events. */
  readonly userEntraId$ = new BehaviorSubject<string | null>(null);
  /** Emits whenever the API confirms a session (sign-in, SSO return, restore). */
  readonly signedIn$ = new Subject<any>();

  constructor(
    private http: HttpClient,
    private router: Router,
    private storage: LocalStorageService,
  ) {
    this.userEntraId$.next(this.readCachedUser()?.userEntraId ?? null);
  }

  /** Makes sure the XSRF-TOKEN cookie exists (call before the first POST). */
  async ensureCsrfToken(): Promise<void> {
    try {
      await firstValueFrom(this.http.get(`${this.authUrl}${AUTH_ROUTES.csrf}`, { context: this.quiet() }));
    } catch {
      // The interceptor retries on the next POST; nothing else to do here.
    }
  }

  /** SQL (user name + password) sign-in. */
  async loginWithPassword(userName: string, userPassword: string): Promise<AuthResult> {
    await this.ensureCsrfToken();
    try {
      const response: any = await firstValueFrom(
        this.http.post(`${this.authUrl}${AUTH_ROUTES.login}`, { userName, userPassword }, { context: this.quiet() })
      );
      if (response?.apiStatus && response?.apiData?.mfaRequired) {
        return { ok: false, mfaRequired: true, mfaEmail: response.apiData.emailId, message: response.apiMessage };
      }
      return this.acceptSession(response);
    } catch (err) {
      return { ok: false, message: this.errorMessage(err, 'Invalid user name or password.') };
    }
  }

  /** Second step for MFA users: the 6-digit PIN from the e-mail. */
  async verifyPin(pin: string): Promise<AuthResult> {
    try {
      const response: any = await firstValueFrom(
        this.http.post(`${this.authUrl}${AUTH_ROUTES.verifyPin}`, { pin }, { context: this.quiet() })
      );
      return this.acceptSession(response);
    } catch (err) {
      return { ok: false, message: this.errorMessage(err, 'Invalid PIN.') };
    }
  }

  /**
   * Starts Microsoft SSO. This leaves the SPA: the API handles the Entra
   * round-trip and redirects back to "/" (or to "/?ssoError=..." on failure).
   */
  startSsoLogin(returnPath: string = '/'): void {
    const safePath = returnPath.startsWith('/') && !returnPath.startsWith('//') ? returnPath : '/';
    window.location.href = `${this.authUrl}${AUTH_ROUTES.ssoLogin}?returnUrl=${encodeURIComponent(safePath)}`;
  }

  /**
   * Asks the API who is signed in (cookie). Used after the SSO redirect and
   * after a page refresh. Returns ok = false when there is no session.
   */
  async restoreSession(): Promise<AuthResult> {
    try {
      const response: any = await firstValueFrom(
        this.http.get(`${this.authUrl}${AUTH_ROUTES.me}`, { context: this.quiet() })
      );
      return this.acceptSession(response);
    } catch (err) {
      if (err instanceof HttpErrorResponse && err.status === 401) {
        return { ok: false };
      }
      return { ok: false, message: this.errorMessage(err, 'Unable to reach the Cereus API.') };
    }
  }

  /**
   * Ends the session on the API (clears the cookie and rotates the CSRF token),
   * clears the local cache and returns to the login page. When the API returns
   * an Entra sign-out URL (SSO users), the browser goes there instead.
   */
  async logout(options: { skipApi?: boolean; stayOnPage?: boolean } = {}): Promise<void> {
    let ssoLogoutUrl: string | null = null;
    if (!options.skipApi) {
      try {
        const response: any = await firstValueFrom(
          this.http.post(`${this.authUrl}${AUTH_ROUTES.logout}`, {}, { context: this.quiet() })
        );
        ssoLogoutUrl = response?.apiData?.ssoLogoutUrl ?? null;
      } catch {
        // Session may already be gone; still clear locally.
      }
    }
    this.clearLocalSession();
    if (options.stayOnPage) {
      return;
    }
    if (ssoLogoutUrl && /^https:\/\//i.test(ssoLogoutUrl)) {
      ssoLogoutUrl = environment.ssoEnabled ? environment.postLogoutUrl : ssoLogoutUrl;
      window.location.href = ssoLogoutUrl;
      return;
    }
    await this.router.navigate(['/']);
  }

  /** Removes the cached profile/permissions without calling the API. */
  clearLocalSession(): void {
    this.storage.removeItem('token');
    this.storage.removeItem('cereus-token');
    this.storage.removeItem('cereus-cache');
    this.storage.clear();
    this.userEntraId$.next(null);
  }

  /** Sends the user to their landing page (same rules the portal used before). */
  navigateToHome(user: any): Promise<boolean> {
    if (user?.isAgreed == 0) {
      return this.router.navigate(['/non-auth/user-agreement']);
    }
    if (user?.domainId === UserDomainType.Internal) {
      switch (user.departmentId) {
        case DepartmentId.Vob:
          return this.router.navigate(['/auth/vob-dashboard']);
        case DepartmentId.Authorization:
          return this.router.navigate(['/auth/auth-dashboard']);
        default:
          return this.router.navigate(['/auth/dashboard']);
      }
    }
    // External users land on the Enterprise report instead of the Facility Dashboard.
    return this.router.navigate(['/auth/report/cereus-overview']);
  }

  // ----------------------------------------------------------------------

  /** Stores the profile + permissions the API returned with the session. */
  private acceptSession(response: any): AuthResult {
    if (!response?.apiStatus || !response?.apiData?.userMasterDetail) {
      return { ok: false, message: response?.apiMessage || 'User not linked with Cereus App. Contact IT Admin!!' };
    }

    const user = response.apiData.userMasterDetail;
    user.isHideFacility = (user.domainId == UserDomainType.External);

    this.storage.removeItem('cereus-token');
    this.storage.setItem('cereus-token', JSON.stringify(user));
    this.storage.removeItem('cereus-cache');
    this.storage.setItem('cereus-cache', JSON.stringify(response.apiData.userPermissionList ?? []));

    this.userEntraId$.next(user.userEntraId ?? response.apiData.userEntraId ?? null);
    this.signedIn$.next(user);
    return { ok: true, user };
  }

  private readCachedUser(): any {
    try {
      const raw = this.storage.getItem('cereus-token');
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  }

  private errorMessage(err: any, fallback: string): string {
    if (err instanceof HttpErrorResponse) {
      if (err.status === 0) return 'Unable to reach the Cereus API.';
      if (err.status === 400) {
        // The API explains CSRF failures in apiData.reason; show it so the cause is visible.
        const reason = err.error?.apiData?.reason;
        return reason ? `Security check failed: ${reason}.` : (err.error?.apiMessage || 'Your session security token expired. Please try again.');
      }
      return err.error?.apiMessage || fallback;
    }
    return fallback;
  }
}
